import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

const MIN_PASSWORD = 8;

export default function SettingsPasswordPage(): JSX.Element {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(t("dashboardUi.auth.errors.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("validation.passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await authClient.changePassword({
        currentPassword: current,
        newPassword: password,
      });
      if (err) {
        setError(translateAuthError(err, t));
        return;
      }
      setDone(true);
      setCurrent("");
      setPassword("");
      setConfirm("");
    } catch (unknownErr) {
      setError(translateAuthError(unknownErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.settings}>{t("dashboardUi.settingsHub.backToAccount")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">{t("dashboardUi.pages.settingsPassword")}</h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">{t("dashboardUi.settingsPasswordForm.subtitle")}</p>

      <div className="mt-8 rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-8 shadow-sm">
        {done ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon name="Check" className="size-7" strokeWidth={1.5} />
            </div>
            <p className="font-body text-body-md text-on-surface">{t("dashboardUi.settingsPasswordForm.successMessage")}</p>
            <button
              type="button"
              className="mt-6 font-semibold text-primary hover:underline"
              onClick={() => setDone(false)}
            >
              {t("dashboardUi.settingsPasswordForm.changeAgain")}
            </button>
          </div>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)} className="space-y-5">
            {error ? (
              <div
                className="rounded-lg bg-error/10 px-4 py-3 text-body-md text-error outline outline-1 outline-error/20"
                role="alert"
              >
                {error}
              </div>
            ) : null}
            <div>
              <label htmlFor="settings-current-password" className="mb-1 block font-label text-label-md text-on-surface">
                {t("dashboardUi.settingsPasswordForm.currentPassword")}
              </label>
              <input
                id="settings-current-password"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="w-full rounded-xl border border-outline-variant/25 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none ring-primary focus:ring-2"
                required
              />
            </div>
            <div>
              <label htmlFor="settings-new-password" className="mb-1 block font-label text-label-md text-on-surface">
                {t("dashboardUi.settingsPasswordForm.newPassword")}
              </label>
              <input
                id="settings-new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-outline-variant/25 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none ring-primary focus:ring-2"
                required
                minLength={MIN_PASSWORD}
              />
            </div>
            <div>
              <label htmlFor="settings-confirm-password" className="mb-1 block font-label text-label-md text-on-surface">
                {t("dashboardUi.settingsPasswordForm.confirmPassword")}
              </label>
              <input
                id="settings-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-xl border border-outline-variant/25 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none ring-primary focus:ring-2"
                required
                minLength={MIN_PASSWORD}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-primary py-3.5 font-headline text-sm font-bold text-on-primary shadow-md transition-opacity disabled:opacity-50"
            >
              {submitting ? t("dashboardUi.profile.saving") : t("dashboardUi.settingsPasswordForm.submit")}
            </button>
            <p className="text-center text-body-md text-on-surface-variant">
              <Link to={dashboardPaths.forgotPassword} className="font-semibold text-primary hover:underline">
                {t("dashboardUi.settingsPasswordForm.forgotPasswordCta")}
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
